import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import { Shield, Lock } from 'lucide-react';
import axios from 'axios';
import PageMeta from '../PageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp]     = useState('');
  const [step, setStep]   = useState('phone');
  const [generatedOTP, setGeneratedOTP] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast({ title: 'Invalid phone', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-otp`, { phone });
      setGeneratedOTP(res.data.otp);  // only present in preview/dev; undefined in prod
      setStep('otp');
      toast({
        title: 'OTP sent on WhatsApp',
        description: `Sent to +91 ${phone} · expires in 5 minutes.`,
        duration: 6000,
      });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      const { access_token, user } = res.data;
      if (user.role !== 'super_admin') {
        toast({
          title: 'Not authorized',
          description: 'This portal is for platform admins only.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_user', JSON.stringify(user));
      toast({ title: 'Welcome back, Admin!' });
      navigate('/admin/dashboard');
    } catch (err) {
      toast({ title: 'Login failed', description: err.response?.data?.detail || 'Invalid OTP', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <PageMeta
        title="Super Admin Portal"
        description="Restricted access. RentKollect super admin sign-in."
        noindex
      />
      <div className="max-w-md w-full">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl" data-testid="admin-login-card">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg">
              <Shield size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">RentKollect</h1>
            <p className="text-amber-300 text-sm font-medium tracking-wider mt-1">SUPER ADMIN PORTAL</p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">Admin phone</label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-white/10 rounded-lg text-white font-medium text-sm">+91</div>
                  <Input
                    data-testid="admin-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit admin number"
                    className="flex-1 bg-white/10 border-white/20 text-white placeholder-white/40"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || phone.length !== 10}
                data-testid="admin-send-otp-btn"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white py-6 text-lg rounded-xl"
              >
                <Lock size={16} className="mr-2" />
                {loading ? 'Sending…' : 'Send OTP'}
              </Button>
              <p className="text-xs text-center text-white/40">Access restricted to platform administrators.</p>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
                  Enter OTP for +91 {phone}
                </label>
                <Input
                  data-testid="admin-otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="bg-white/10 border-white/20 text-white text-center tracking-widest text-lg"
                  placeholder="••••••"
                  maxLength={6}
                  required
                />
                {/* OTP is delivered via WhatsApp — no demo display */}
              </div>
              <Button type="submit" disabled={loading || otp.length !== 6} data-testid="admin-verify-btn"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white py-6 text-lg rounded-xl">
                {loading ? 'Verifying…' : 'Enter Admin Portal'}
              </Button>
              <Button type="button" onClick={() => { setStep('phone'); setOtp(''); }} variant="outline" className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10">
                Change Number
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
